import { IPromise } from 'angular';
import * as React from 'react';
import { call, put, select } from 'redux-saga/effects';
import * as _ from 'underscore';

import { ShareDialog, ShareDialogProps } from '@tableau/share-dialog';
import { DialogPositioningBehavior, DialogType } from '@tableau/widgets';

import { Messages } from '../../../../codegen/Localize';
import { ShareDialogUserActionOptions, ShareDialogUserActionsEvent, TelemetryEvents } from '../../../../common/telemetryEvents/TelemetryEvents';
import { DatasourceContentType } from '../../../../ts/models/ContentTypes';
import { IDataSourceInfo } from '../../../../ts/models/IDataSourceInfo';
import { IViewDetailInfo } from '../../../../ts/models/IViewInfo';
import { IExplorableItemTypeInfo } from '../../../../ts/models/resourceInfoParts';
import { Views } from '../../../../ts/resources/Views';
import { ModalInstance, ReactModalOptions, ReactModalService } from '../../../../ts/services/ReactModalService';
import { Telemetry } from '../../../../commonInfra/services/Telemetry';
import { IToastOptions, ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { ShareContentAction } from '../../../actions/spaces/ShareContentAction';
import { SessionSiteSelectors } from '../../../selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '../../../selectors/SiteUrlsSelectors';
import { ServiceLocator } from '../../../ServiceLocator';
import {
  datasourceShareUrl,
  flowShareUrl,
  metricShareUrl,
  projectShareUrl,
  publishedConnectionShareUrl,
  ShareOrigin,
  viewShareUrl,
  workbookShareUrl,
  collectionShareUrl,
} from '../../../spaces/ShareUrls';
import { ICollectionInfo } from '../../../spaces/Collections/ICollectionInfo';
import { SharePrivateCollectionWarningDialog } from '../../../spaces/Collections/SharePrivateCollectionWarningDialog';
import { reloadSpace } from './RefreshContentSagas';
import { getDataConnectionId } from '../SpacesSagaHelpers';

const shareDialogTestId = 'share-dialog';

/*
* getBaseDialogOptions() - constant properties for share dialog which are reused in tests
* Why it's method: https://tableau.slack.com/archives/C9H9ERB5G/p1553880792040700
*/
const getBaseDialogOptions = () => {
  return {
    testId: shareDialogTestId,
    type: DialogType.NormalWithTitleBar,
    dialogWidth: 500,
    showCloseButton: true,
    positioningBehavior: DialogPositioningBehavior.Draggable,
    shouldReturnFocusOnClose: true,
  };
};

const popToast = (toast: IToastOptions): void => {
  const service = ToasterService.instance;
  service.pop(toast);
};

/**
 * Callback method to show toast in VizportalClient.
 * @param message toast message text sent from share-dialog
 */
const toastHandler = (message: string, isInfo: boolean): void => {
  popToast({
    text: message,
    type: isInfo ? ToastType.Success : ToastType.Error,
  });
};

const logEvent = (event: ShareDialogUserActionsEvent): void => {
  Telemetry.sendEvent<ShareDialogUserActionsEvent>(
    {
      type: event.type,
      UserActionType: event.UserActionType,
      Args: event.Args
    }
  );
};

/**
 * Wrapper function for telemetry callback. Share dialog calls this function, to log user
 * actions to PUT database, when user clicks on copy link, share button etc
 * @param userActionType One of the user action in ShareDialogUserActionOptions
 * @param args (optional) params
 */
const telemetryLogger = (userActionType: ShareDialogUserActionOptions, args?: string): void => {
  logEvent({ type: TelemetryEvents.ShareDialogUserActions, UserActionType: userActionType, Args: args });
};

let modalInstance: ModalInstance;
/**
 * Callback method to close dialog in VizportalClient.
 */
const closeDialogHandler = (): void => {
  modalInstance.close();

  telemetryLogger(ShareDialogUserActionOptions.CloseShareDialog);
};

function* handleShareContentAction(action: ShareContentAction) {
  try {
    const content = action.content;

    const siteUrls = yield select(siteUrlsSelector);
    const isNamedSharingEnabled = yield select(SessionSiteSelectors.isNamedSharingEnabled);
    let shareLink: string;
    let title: string;
    let contentId = content.id;

    switch (content.contentType) {
      case 'view':
      case 'recommendedView':
        const viewDetailInfo: IViewDetailInfo = yield call(getViewDetailInfo, content);
        shareLink = yield call(viewShareUrl, siteUrls, viewDetailInfo.path, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForView();
        break;
      case 'project':
        shareLink = yield call(projectShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForProject();
        break;
      case 'workbook':
        shareLink = yield call(workbookShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForWorkbook();
        break;
      case 'datasource':
        // Datasource has two ids - datasource id and data connection id. For sharing to work properly,
        // the datasource id should be passed as contentId and the data connection id should be used to
        // generate shareLink. The fix for DEFECT 988384 tried to address this issue, but because Explore
        // page and Project page uses different APIs to fetch content, there are two different cases of
        // content passed to ShareContentAction:
        //
        // 1. content = {
        //    id = datasource id
        // }
        //
        // 2. content = {
        //    idForFavoriting = datasource id,
        //    id = data connection id
        // }
        //
        // The second case works. To fix DEFECT 1120191, in the first case, extra API call is required.
        const idForFavoriting = (content as IDataSourceInfo).idForFavoriting;
        if (!idForFavoriting) {
          const dataConnectionId = yield call(getDataConnectionId, content.id);
          contentId = content.id;
          shareLink = yield call(datasourceShareUrl, siteUrls, dataConnectionId, ShareOrigin.CardLink);
        } else {
          contentId = idForFavoriting;
          shareLink = yield call(datasourceShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        }
        title = Messages.shareDialogTitleForDatasource();
        break;
      case 'flow':
        shareLink = yield call(flowShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForFlow();
        break;
      case 'metric':
        shareLink = yield call(metricShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForMetric();
        break;
      case 'publishedConnection':
        shareLink = yield call(publishedConnectionShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForPublishedConnection();
        break;
      case 'collection':
        if ((content as ICollectionInfo).visibility === 'private') {
          const closeReason = yield call(SharePrivateCollectionWarningDialog.open, content as ICollectionInfo);
          if (_.isUndefined(closeReason) || closeReason === SharePrivateCollectionWarningDialog.RESULT_PRIVATE) {
            return;
          } else {
            yield call(reloadSpace);
          }
        }
        shareLink = yield call(collectionShareUrl, siteUrls, content.luid, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForCollection();
        break;
      default:
        return;
    }

    const shareDialogProps: ShareDialogProps = {
      closeDialogHandler,
      contentId,
      contentName: content.name,
      contentType: content.contentType,
      encodedShareLink: shareLink,
      isNamedSharingEnabled,
      toastHandler: toastHandler,
      shouldShareThumbnail: true, // When sharing from VizPortal, we always want to share the thumbnail for a view, not a VizSnapshot
      logEvent: telemetryLogger
    };

    const dialogOptions: ReactModalOptions = {
      ...getBaseDialogOptions(),
      title,
      dialogContent: React.createElement(ShareDialog, shareDialogProps)
    };

    modalInstance = yield call(ReactModalService.open, dialogOptions);

    // Log for what entity did we open the share dialog
    yield call(telemetryLogger, ShareDialogUserActionOptions.OpenShareDialog, content.contentType);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getViewDetailInfo(content: IExplorableItemTypeInfo): IPromise<IViewDetailInfo> {
  const resource = ServiceLocator.get<Views>('Views');
  return resource.getById(content.id);
}

export { closeDialogHandler, getBaseDialogOptions, handleShareContentAction, telemetryLogger, toastHandler };
